import { useMemo, useState } from 'react'
import {
  useAccessKeys, useDeleteAccessKey,
} from '../../api'
import { ConfirmDialog } from '../../components/ConfirmDialog'
import { CreateAccessKeyDialog } from '../../components/CreateAccessKeyDialog'
import { EditAccessKeyDialog } from '../../components/EditAccessKeyDialog'
import { RegenerateButton } from '../../components/RegenerateButton'
import { PhoneStateCard } from '../../components/PhoneStateCard'
import { Pagination } from '../../components/Pagination'
import { AccessKeyCell } from '../../components/AccessKeyCell'
import { useNotify } from '../../components/Notifications'
import { formatDateTime } from '../../lib/format'
import {
  sortAccessKeys,
  type AccessKeySortState,
} from '../../lib/sortAccessKeys'
import type { AccessKeySummary } from '../../types'

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; userId: string }
  | { type: 'delete'; row: AccessKeySummary }

const PAGE_SIZE = 20

/**
 * A key search only ever matches the visible 4-char prefix: the plaintext
 * key never reaches the client, `masked_key` ("XK4P-••••-••••") is all
 * there is to match against.
 */
function searchKeys(keys: AccessKeySummary[], q: string): AccessKeySummary[] {
  if (!q.trim()) return keys
  const needle = q.trim().toLowerCase()
  return keys.filter(k =>
    (k.label?.toLowerCase().includes(needle) ?? false) ||
    k.masked_key.toLowerCase().includes(needle),
  )
}

function LastLogin({ iso }: { iso: string | null }) {
  if (!iso) return <span className="text-gray-400 dark:text-gray-600">—</span>
  return <span>{formatDateTime(iso)}</span>
}

/**
 * Single-phone keys show the Pixel thumbnail at the row head so admin can
 * visually skim online/offline status without reading the counter. Multi-
 * phone keys get a compact counter badge instead — a single thumbnail
 * would be misleading (which phone's state?).
 *
 * `online_count` is the only live signal we have in the list response;
 * anything richer (in-call / ringing) would need a join on active call
 * state which isn't worth the round-trip for a skim view.
 */
function RowThumbnail({ row }: { row: AccessKeySummary }) {
  if (row.phone_count === 1) {
    const state = row.online_count >= 1 ? 'normal' : 'offline'
    return <PhoneStateCard state={state} size="sm" />
  }
  return (
    <div
      aria-label={`${row.phone_count} phones`}
      className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm font-semibold text-gray-600 dark:text-gray-300"
    >
      {row.phone_count}×
    </div>
  )
}

export function AccessKeysPage() {
  const { data: keys = [], isLoading, error } = useAccessKeys()
  const deleteMutation = useDeleteAccessKey()
  const notify = useNotify()
  const [modal, setModal] = useState<ModalState>({ type: 'none' })
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  // Default ordering: by label ascending with natural collation so admin
  // sees `g9, g10, s9, s11` grouped instead of the backend's created_at
  // desc order which scatters similarly-named keys.
  const [sort, setSort] = useState<AccessKeySortState>({
    key: 'label',
    dir: 'asc',
  })

  const filtered = useMemo(() => searchKeys(keys, search), [keys, search])
  const sorted = useMemo(() => sortAccessKeys(filtered, sort), [filtered, sort])
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  // Clamp: deleting rows or narrowing the search can drop the total page
  // count below the page we're currently on.
  const effectivePage = Math.min(page, totalPages)
  const pageRows = sorted.slice(
    (effectivePage - 1) * PAGE_SIZE,
    effectivePage * PAGE_SIZE,
  )

  const toggleLabelSort = () => {
    setSort(prev => ({
      key: 'label',
      dir: prev.dir === 'asc' ? 'desc' : 'asc',
    }))
    setPage(1)
  }

  const close = () => setModal({ type: 'none' })

  const confirmDelete = () => {
    if (modal.type !== 'delete') return
    deleteMutation.mutate(modal.row.id, {
      onSuccess: close,
      onError: err => notify(err.message || 'Ошибка удаления', 'error'),
    })
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Access Keys</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {keys.length} {keys.length === 1 ? 'ключ' : 'ключей'}
          </p>
        </div>
        <button
          onClick={() => setModal({ type: 'create' })}
          className="text-sm font-medium px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
        >
          + Новый ключ
        </button>
      </div>

      {isLoading && (
        <p className="text-gray-400 dark:text-gray-500 text-center py-12">Загрузка…</p>
      )}
      {error && (
        <p className="text-red-500 text-center py-12">{error.message}</p>
      )}
      {!isLoading && !error && keys.length === 0 && (
        <div className="text-center py-16">
          <p className="text-gray-400 dark:text-gray-500 mb-1">Пока ни одного ключа</p>
          <p className="text-sm text-gray-400 dark:text-gray-600">
            Создайте ключ и привяжите к нему свободные трубки.
          </p>
        </div>
      )}

      {!isLoading && !error && keys.length > 0 && (
        <>
          <div className="mb-3">
            <input
              type="search"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Поиск по метке или ключу…"
              aria-label="Поиск ключей"
              className="w-full sm:max-w-sm text-sm px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 focus:outline-none focus:border-blue-500"
            />
          </div>

          {pageRows.length === 0 ? (
            <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-12">
              Ничего не найдено
            </p>
          ) : (
            <>
              {totalPages > 1 && (
                <div className="mb-4">
                  <Pagination
                    currentPage={effectivePage}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900">
                    <tr className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      <th className="px-4 py-3 w-20" aria-label="Превью" />
                      <th
                        className="px-4 py-3 text-left cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200"
                        onClick={toggleLabelSort}
                        aria-sort={sort.dir === 'asc' ? 'ascending' : 'descending'}
                      >
                        Метка{sort.dir === 'asc' ? ' ↑' : ' ↓'}
                      </th>
                      <th className="px-4 py-3 text-left">Ключ</th>
                      <th className="px-4 py-3 text-center">Трубки</th>
                      <th className="px-4 py-3 text-left">Последний вход</th>
                      <th className="px-4 py-3 text-left">Создан</th>
                      <th className="px-4 py-3 text-right">Действия</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-950">
                    {pageRows.map(row => (
                      <tr
                        key={row.id}
                        onClick={() => setModal({ type: 'edit', userId: row.id })}
                        className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-900/50 cursor-pointer"
                      >
                        <td className="px-4 py-2">
                          <RowThumbnail row={row} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">
                          {row.label ?? <span className="text-gray-400 italic">без метки</span>}
                        </td>
                        <td className="px-4 py-3">
                          <AccessKeyCell userId={row.id} maskedKey={row.masked_key} />
                        </td>
                        <td className="px-4 py-3 text-sm text-center">
                          <span className="text-gray-900 dark:text-gray-100 font-medium">{row.online_count}</span>
                          <span className="text-gray-400 dark:text-gray-600"> / {row.phone_count}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
                          <LastLogin iso={row.key_last_login_at} />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                          {formatDateTime(row.created_at)}
                        </td>
                        <td
                          className="px-4 py-3 text-right whitespace-nowrap"
                          onClick={e => e.stopPropagation()}
                        >
                          <div className="inline-flex gap-2">
                            <RegenerateButton userId={row.id} label={row.label} />
                            <button
                              onClick={() => setModal({ type: 'delete', row })}
                              className="text-xs font-medium px-2.5 py-1 rounded border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                            >
                              Удалить
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={effectivePage}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      <CreateAccessKeyDialog
        open={modal.type === 'create'}
        onClose={close}
      />

      <EditAccessKeyDialog
        open={modal.type === 'edit'}
        userId={modal.type === 'edit' ? modal.userId : null}
        onClose={close}
      />

      {modal.type === 'delete' && (
        <ConfirmDialog
          open
          onClose={close}
          onConfirm={confirmDelete}
          title="Удалить ключ?"
          message={`${modal.row.label ? `«${modal.row.label}». ` : ''}Трубки станут бесхозными (user_id=NULL), их нужно будет привязать к новому ключу. Оператор потеряет доступ немедленно.`}
          confirmLabel="Удалить"
          isPending={deleteMutation.isPending}
        />
      )}
    </div>
  )
}
